<script lang="ts" setup>
import { computed } from 'vue'
import { Dropdown, Menu, MenuItem } from 'ant-design-vue'

interface MenuItemConfig {
  key: string
  label: string
  icon: string
  onClick: (id: string) => void
  color: string
  isLoading?: boolean
  isDisabled?: boolean
}

interface Props {
  menuItems: MenuItemConfig[]
  isDisabled?: boolean
  placement?: 'bottomRight' | 'topRight' | 'bottomLeft' | 'topLeft'
  trigger?: 'click' | 'hover' | 'contextmenu'
  contextMenuTriggerStyle?: string
}

interface Emits {
  (e: 'open-change', isOpen: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'bottomRight',
  trigger: 'click',
  isDisabled: false
})

const emit = defineEmits<Emits>()

const isAnyMenuItemLoading = computed(() =>
    props.menuItems.some((item) => item.isLoading)
)

const handleVisibleChange = (visible: boolean) => {
  emit('open-change', visible)
}
</script>

<template>
  <Dropdown
      :trigger="props.trigger"
      :placement="props.placement"
      :disabled="props.isDisabled"
      @visible-change="handleVisibleChange"
  >
    <button class="ContextMenu__trigger" :disabled="props.isDisabled" @click.stop>
      <div v-if="isAnyMenuItemLoading" class="ContextMenu__spinner"></div>
      <slot v-else name="trigger">
        <div class="ContextMenu__defaultTrigger"></div>
      </slot>
    </button>

    <template #overlay>
      <Menu class="ContextMenu__menu">
        <MenuItem
            v-for="{ label, onClick, icon, key, color, isLoading, isDisabled } in props.menuItems"
            @click="() => !isLoading && !props.isDisabled && !isDisabled && onClick(key)"
            :key="key"
            :disabled="isLoading || props.isDisabled || isDisabled"
        >
          <div :class="['ContextMenu__menuItem', isDisabled && 'ContextMenu__menuItem_disabled']">
            <div v-if="isLoading" class="ContextMenu__menuItemSpinner"></div>
            <img v-else :src="icon" :alt="`${label} icon`" class="ContextMenu__menuItemImage" />

            <div :style="{ color }">
              {{ isLoading ? `${label}...` : label }}
            </div>
          </div>
        </MenuItem>
      </Menu>
    </template>
  </Dropdown>
</template>

<style lang="css" scoped>
.ContextMenu__trigger {
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
}

.ContextMenu__trigger:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.ContextMenu__defaultTrigger {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: var(--gray_60);
  box-shadow:
      0 -6px 0 var(--gray_60),
      0 6px 0 var(--gray_60);
  transform: rotate(90deg);

}

.ContextMenu__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--gray_30);
  border-top-color: var(--gray_80);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.ContextMenu__menuItemSpinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--gray_30);
  border-top-color: var(--gray_80);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.ContextMenu__menuItemImage {
  width: 16px;
  height: 16px;
}

.ContextMenu__menu {
  border: 1px solid var(--gray_30);
  border-radius: 6px;
  width: 220px;
}

.ContextMenu__menuItem {
  display: flex;
  align-items: center;
  gap: 14px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>